import { useState } from 'react'
import {
  Badge,
  Button,
  Group,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Alert,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import dayjs from 'dayjs'
import {
  useExpandirOcorrencias,
  useOcorrenciasList,
} from '../../hooks/useOcorrencias'
import { STATUS_OCORRENCIA, labelDe } from '../planos_modelo/constants'
import { extractApiError } from '../../lib/extractApiError'
import RegistrarExecucaoModal from './RegistrarExecucaoModal'
import type { PMOC } from '../../types'
import type { Ocorrencia } from '../../types/pmocPlanos'

interface Props {
  pmoc: PMOC
}

export default function PmocOcorrenciasTab({ pmoc }: Props) {
  const hoje = dayjs()
  const [dataIni, setDataIni] = useState(hoje.format('YYYY-MM-DD'))
  const [dataFim, setDataFim] = useState(hoje.add(90, 'day').format('YYYY-MM-DD'))
  const [filtroStatus, setFiltroStatus] = useState<string | null>(null)
  const [somenteVencidas, setSomenteVencidas] = useState(false)
  const [registrarOco, setRegistrarOco] = useState<Ocorrencia | null>(null)

  const lista = useOcorrenciasList(pmoc.id, {
    data_inicial: dataIni,
    data_final: dataFim,
    status: filtroStatus ?? undefined,
    somente_vencidas: somenteVencidas || undefined,
  })
  const expandir = useExpandirOcorrencias(pmoc.id)

  async function handleExpandir() {
    try {
      const r = await expandir.mutateAsync({
        data_inicial: dataIni,
        data_final: dataFim,
      })
      notifications.show({
        color: 'green',
        title: 'Ocorrências expandidas',
        message: `${r.criadas} novas · ${r.atualizadas} atualizadas · ${r.ja_existentes} já existentes`,
      })
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Falha ao expandir',
        message: extractApiError(err, 'Erro ao expandir ocorrências.'),
      })
    }
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start">
        <div>
          <Title order={4}>Ocorrências do PMOC</Title>
          <Text size="xs" c="dimmed">
            Cada ocorrência é uma atividade prevista em uma data específica.
            Use "Expandir" para gerar as ocorrências do período.
          </Text>
        </div>
      </Group>

      <Paper p="sm" withBorder>
        <Group align="flex-end" wrap="wrap" gap="sm">
          <TextInput
            label="De"
            type="date"
            value={dataIni}
            onChange={(e) => setDataIni(e.currentTarget.value)}
            w={160}
          />
          <TextInput
            label="Até"
            type="date"
            value={dataFim}
            onChange={(e) => setDataFim(e.currentTarget.value)}
            w={160}
          />
          <Select
            label="Status"
            placeholder="Todos"
            clearable
            data={STATUS_OCORRENCIA}
            value={filtroStatus}
            onChange={setFiltroStatus}
            w={200}
          />
          <Button
            variant={somenteVencidas ? 'filled' : 'default'}
            color="red"
            onClick={() => setSomenteVencidas((v) => !v)}
          >
            {somenteVencidas ? 'Vencidas ✓' : 'Só vencidas'}
          </Button>
          <Button
            onClick={handleExpandir}
            loading={expandir.isPending}
            data-testid="expandir-ocorrencias-btn"
          >
            ⟳ Expandir período
          </Button>
        </Group>
      </Paper>

      {pmoc.status === 'rascunho' && (
        <Alert color="blue" variant="light">
          PMOC em rascunho — ocorrências geradas aqui são exploratórias. Após
          ativar o PMOC, as ocorrências passam a contar como compromisso oficial
          do plano.
        </Alert>
      )}

      {lista.isLoading ? (
        <Text c="dimmed">Carregando ocorrências…</Text>
      ) : (lista.data?.results.length ?? 0) === 0 ? (
        <Paper p="lg" withBorder>
          <Text c="dimmed">
            Nenhuma ocorrência neste período. Clique "Expandir período" para
            gerar a partir dos planos aplicados.
          </Text>
        </Paper>
      ) : (
        <Paper withBorder>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Data prevista</Table.Th>
                <Table.Th>Atividade</Table.Th>
                <Table.Th>Categoria</Table.Th>
                <Table.Th>Alvo</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>OS</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {lista.data?.results.map((o) => {
                const statusCfg = STATUS_OCORRENCIA.find(
                  (s) => s.value === o.status,
                )
                return (
                  <Table.Tr key={o.id}>
                    <Table.Td>
                      {o.data_prevista}
                      {o.vencida && (
                        <Badge size="xs" color="red" ml="xs">
                          vencida
                        </Badge>
                      )}
                    </Table.Td>
                    <Table.Td>{o.atividade_nome}</Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">
                        {o.categoria}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">
                        {o.equipamento
                          ? `equip ${o.equipamento.slice(0, 8)}`
                          : o.ambiente
                            ? `amb ${o.ambiente.slice(0, 8)}`
                            : o.site
                              ? `site ${o.site.slice(0, 8)}`
                              : '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        size="sm"
                        style={{
                          background: statusCfg?.cor ?? '#90A4AE',
                          color: 'white',
                        }}
                      >
                        {labelDe(o.status, STATUS_OCORRENCIA)}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      {o.ordem_servico ? (
                        <Text size="xs" c="dimmed">
                          OS #{o.ordem_servico.slice(0, 8)}
                        </Text>
                      ) : (
                        <Text size="xs" c="dimmed">
                          —
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Button
                        size="xs"
                        variant="light"
                        onClick={() => setRegistrarOco(o)}
                      >
                        Execução
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                )
              })}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      {lista.data && lista.data.count > lista.data.results.length && (
        <Text size="xs" c="dimmed">
          Mostrando {lista.data.results.length} de {lista.data.count} ocorrências.
          Refine o filtro de datas/status para reduzir.
        </Text>
      )}

      <RegistrarExecucaoModal
        ocorrencia={registrarOco}
        onClose={() => setRegistrarOco(null)}
      />
    </Stack>
  )
}
